import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, doc, endAt, getDoc, getDocs, onSnapshot, orderBy, query, startAfter, limit } from 'firebase/firestore';
import { db } from '../services/firebase';
import { toMillis } from '../utils/timestamps';

const PAGE_SIZE = 100;
const CHECK_CONCURRENCY = 4;
const emptyState = () => ({ uploads: [], visibleUploads: [], loading: true, checking: 0, hasOlder: false, error: null });

// Visibility is judged on first sight; work that finishes while this page is
// open stays visible as confirmation. Workspace completion means routing finished.
// Destination batches decide whether ingestion is finished too; failed members
// remain visible until removed.
function boundaryRetired(boundary) {
  return boundary.status === 'completed'
    || (boundary.status === 'completed_with_errors'
      && !Object.values(boundary.raw_manifest || {}).includes('error'));
}

export default function useWorkspaceUploads(companyId, workspaceFiles) {
  const [state, setState] = useState(emptyState);
  const sessionRef = useRef(null);
  const pinnedIds = useMemo(() => [...new Set(workspaceFiles.map(file => file.upload_id).filter(Boolean))]
    .sort().join('|'), [workspaceFiles]);

  useEffect(() => {
    setState(emptyState());
    if (!companyId) return undefined;
    const uploadsCollection = collection(db, `users/${companyId}/uploads`);
    const session = {
      active: true, documents: new Map(), decisions: new Map(), visible: new Set(),
      queue: [], running: 0, subscriptions: [], extraIds: new Map(), pinned: new Set(),
      cursor: null, firstPage: true, loading: false, hasOlder: true, pageError: null,
    };
    sessionRef.current = session;

    function publish() {
      if (!session.active) return;
      const uploads = [...session.documents.values()]
        .sort((a, b) => toMillis(b.created_at) - toMillis(a.created_at));
      const checkFailed = [...session.decisions.values()].includes('error');
      const watchFailed = session.subscriptions.some(subscription => subscription.error);
      setState({
        uploads, visibleUploads: uploads.filter(upload => session.visible.has(upload.id)),
        loading: session.loading, checking: session.running + session.queue.length,
        hasOlder: session.hasOlder,
        error: session.pageError || (watchFailed ? 'Batch updates could not be loaded. Retry to reconnect.' : null)
          || (checkFailed ? 'Some batches could not be checked. Retry to load them.' : null),
      });
    }

    function watch(target, receive) {
      const subscription = { stop: null, error: null, connect: null };
      subscription.connect = () => {
        subscription.stop?.();
        subscription.error = null;
        subscription.stop = onSnapshot(target, snapshot => {
          if (!session.active) return;
          receive(snapshot);
          checkNext();
          publish();
        }, error => {
          if (!session.active) return;
          console.error('Could not watch workspace uploads:', error);
          subscription.error = error;
          publish();
        });
      };
      session.subscriptions.push(subscription);
      subscription.connect();
      return subscription;
    }

    function checkNext() {
      while (session.active && session.running < CHECK_CONCURRENCY && session.queue.length > 0) {
        const upload = session.queue.shift();
        session.running += 1;
        checkUpload(upload).finally(() => {
          session.running -= 1;
          checkNext();
          publish();
        });
      }
    }

    async function checkUpload(upload) {
      try {
        let relevant = !['completed', 'completed_with_errors'].includes(upload.status)
          || Object.values(upload.raw_manifest || {}).some(status => status === 'pending' || status === 'error');
        if (!relevant) {
          const tableIds = [...new Set(Object.values(upload.routing || {})
            .flatMap(route => route.table_ids || (route.table_id ? [route.table_id] : [])))];
          const children = await Promise.all(tableIds.map(tableId => getDoc(
            doc(db, `users/${companyId}/tables/${tableId}/uploads/${upload.id}`))));
          relevant = children.some(child => !child.exists() || !boundaryRetired(child.data()));
        }
        if (!session.active) return;
        // A staging card is direct evidence of unfinished work, even if its
        // listener arrived while this historical batch was being checked.
        if (relevant || session.pinned.has(upload.id)) session.visible.add(upload.id);
        session.decisions.set(upload.id, 'done');
      } catch (error) {
        if (!session.active) return;
        console.error('Could not check workspace upload:', error);
        session.decisions.set(upload.id, 'error');
      }
    }

    function accept(snapshot) {
      const upload = { ...snapshot.data(), id: snapshot.id };
      session.documents.set(upload.id, upload);
      if (!session.decisions.has(upload.id)) {
        session.decisions.set(upload.id, 'checking');
        session.queue.push(upload);
      }
    }

    session.loadPage = async () => {
      if (session.loading || !session.hasOlder) return;
      session.loading = true;
      session.pageError = null;
      publish();
      const previousCursor = session.cursor;
      const filters = [orderBy('created_at', 'desc')];
      if (previousCursor) filters.push(startAfter(previousCursor));
      try {
        const page = await getDocs(query(uploadsCollection, ...filters, limit(PAGE_SIZE)));
        if (!session.active) return;
        page.docs.forEach(accept);
        const last = page.docs[page.docs.length - 1];
        // Watch a fixed range, not a moving limit: new batches cannot push an
        // unfinished batch out of the subscription. Each older page is disjoint.
        if (last || session.firstPage) {
          const range = [...filters];
          if (last) range.push(endAt(last));
          let knownIds = new Set(page.docs.map(snapshot => snapshot.id));
          watch(query(uploadsCollection, ...range), snapshot => {
            const currentIds = new Set(snapshot.docs.map(item => item.id));
            for (const id of knownIds) {
              if (currentIds.has(id)) continue;
              if (session.pinned.has(id)) session.documents.set(id, { id, status: 'missing' });
              else {
                session.documents.delete(id);
                session.visible.delete(id);
              }
            }
            snapshot.docs.forEach(accept);
            knownIds = currentIds;
          });
        }
        if (last) session.cursor = last;
        session.firstPage = false;
        session.hasOlder = page.size === PAGE_SIZE;
        checkNext();
      } catch (error) {
        if (!session.active) return;
        console.error('Could not load workspace uploads:', error);
        session.pageError = 'Batches could not be loaded. Retry to load them.';
      } finally {
        session.loading = false;
        publish();
      }
    };

    session.includeStagingBatches = ids => {
      session.pinned = new Set(ids);
      for (const [id, subscription] of session.extraIds) {
        if (!session.pinned.has(id) && session.documents.get(id)?.status === 'missing') {
          subscription.stop();
          session.subscriptions = session.subscriptions.filter(item => item !== subscription);
          session.extraIds.delete(id);
          session.documents.delete(id);
          session.visible.delete(id);
        }
      }
      ids.forEach(id => {
        session.visible.add(id);
        if (session.documents.has(id) || session.extraIds.has(id)) return;
        // Staging is watched without a historical limit. Its batches must be
        // readable too, including approvals older than the currently loaded pages.
        const subscription = watch(doc(db, `users/${companyId}/uploads/${id}`), snapshot => {
          if (snapshot.exists()) accept(snapshot);
          else session.documents.set(id, { id, status: 'missing' });
        });
        session.extraIds.set(id, subscription);
      });
      publish();
    };
    session.retry = () => {
      session.subscriptions.filter(subscription => subscription.error).forEach(subscription => subscription.connect());
      for (const [id, decision] of session.decisions) {
        if (decision !== 'error') continue;
        const upload = session.documents.get(id);
        if (!upload) continue;
        session.decisions.set(id, 'checking');
        session.queue.push(upload);
      }
      checkNext();
      if (session.pageError && session.hasOlder) session.loadPage();
      publish();
    };
    session.loadPage();
    return () => {
      session.active = false;
      session.subscriptions.forEach(subscription => subscription.stop());
      if (sessionRef.current === session) sessionRef.current = null;
    };
  }, [companyId]);

  useEffect(() => {
    sessionRef.current?.includeStagingBatches(pinnedIds ? pinnedIds.split('|') : []);
  }, [companyId, pinnedIds]);

  const loadOlder = useCallback(() => sessionRef.current?.loadPage(), []);
  const retry = useCallback(() => sessionRef.current?.retry(), []);
  return { ...state, loadOlder, retry };
}
